import { useWindowResize } from '@toggle/helpers';
import { NullableTransformedXAxisType } from '@toggle/toggle';
import { getInstanceByDom, init } from 'echarts';
import React, { useEffect, useRef } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  SingleSeries,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { AxisPointerEvent, generateDoughnutSeries } from './utils';

export interface DoughnutChartProps extends ApacheChartBase {
  chartType?: 'apache-donut';
  xAxis: NullableTransformedXAxisType;
  series: SingleSeries[];
}

export const DoughnutChart = ({
  theme = 'dark_default',
  chartType = 'apache-donut',
  xAxis,
  series,
  onInit,
  isModal,
}: DoughnutChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef: ref });

  const isShowLineChart =
    series.some(i => i.data.length > 1) && !!xAxis?.length;

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(ref.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        animationDuration: 2_000,
        xAxis: {
          type: 'category',
          data: xAxis ?? [],
          show: isShowLineChart,
        },
        yAxis: {
          show: isShowLineChart,
          boundaryGap: ['5%', '5%'],
        },
        ...(isShowLineChart && {
          grid: { top: '50%' },
        }),
        series: generateDoughnutSeries({
          series,
          dataIndex: 0,
          isShowLineChart,
          chartWidth: width || DEFAULT_CHART_WIDTH,
          theme,
        }),
        legend: {
          show: false,
        },
        tooltip: {
          trigger: 'axis',
          showContent: true,
        },
      },
    });

    chartDom.setOption(option);

    if (ref.current) {
      onInit?.(ref.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [chartType, width, height, isModal]);

  useEffect(() => {
    if (ref.current) {
      const chartDom = getInstanceByDom(ref.current);

      const handleUpdateAxisPointer = (event: AxisPointerEvent) => {
        const xAxisInfo = event.axesInfo[0];

        if (xAxisInfo) {
          chartDom?.setOption({
            series: generateDoughnutSeries({
              series,
              dataIndex: xAxisInfo.value,
              isShowLineChart,
              chartWidth: width || DEFAULT_CHART_WIDTH,
              theme,
            }),
          });
        }
      };

      const listener = (event: unknown) =>
        handleUpdateAxisPointer(event as AxisPointerEvent);
      chartDom?.on('updateAxisPointer', listener);

      return () => {
        chartDom?.off('updateAxisPointer', listener);
      };
    }

    return undefined;
  }, [chartType, series]);

  return <div ref={ref} />;
};
